// The photo lightbox. Port of public/assets/js/lightbox.js.
//
// Deliberately small: one overlay reused for every open, appended to <body>
// rather than to the view's outlet. The router calls `outlet.replaceChildren()`
// on navigation, so an overlay living inside the outlet would be torn out
// mid-animation and leave the scroll lock behind.

import { el } from '../dom.js';
import type { Photo } from '@msc/shared';

let overlay: HTMLElement | null = null;
let photos: Photo[] = [];
let index = 0;

/** Open the lightbox on `photos[start]`, with arrows across the whole set. */
export function openLightbox(all: Photo[], start: number): void {
  photos = all;
  index = start;
  ensureOverlay();
  render();
  overlay!.hidden = false;
  // Stop the page behind from scrolling under the overlay on touch.
  document.body.classList.add('lightbox-open');
}

function close(): void {
  if (overlay) overlay.hidden = true;
  document.body.classList.remove('lightbox-open');
}

function step(delta: number): void {
  if (photos.length === 0) return;
  // Wraps, so the arrows never dead-end on a long roll.
  index = (index + delta + photos.length) % photos.length;
  render();
}

function ensureOverlay(): void {
  if (overlay) return;

  const img = el('img', { class: 'lightbox-img', alt: '' });
  const caption = el('p', { class: 'lightbox-caption' });
  const counter = el('span', { class: 'lightbox-counter' });

  const prev = el('button', { class: 'lightbox-nav lightbox-prev', 'aria-label': 'Previous' }, '‹');
  const next = el('button', { class: 'lightbox-nav lightbox-next', 'aria-label': 'Next' }, '›');
  const closeBtn = el('button', { class: 'lightbox-close', 'aria-label': 'Close' }, '×');

  prev.addEventListener('click', (ev) => {
    ev.stopPropagation();
    step(-1);
  });
  next.addEventListener('click', (ev) => {
    ev.stopPropagation();
    step(1);
  });
  closeBtn.addEventListener('click', close);

  const frame = el('div', { class: 'lightbox-frame' }, [img, caption, counter]);
  // Clicking the backdrop closes; clicking the image itself must not, or every
  // attempt to look closely dismisses the thing you are looking at.
  frame.addEventListener('click', (ev) => ev.stopPropagation());

  overlay = el('div', { class: 'lightbox', hidden: true, role: 'dialog', 'aria-modal': 'true' }, [
    closeBtn,
    prev,
    frame,
    next,
  ]);
  overlay.addEventListener('click', close);

  // One document-level key handler for the lifetime of the page. Bound here
  // rather than on open, so repeated opens do not stack listeners.
  document.addEventListener('keydown', (ev) => {
    if (!overlay || overlay.hidden) return;
    if (ev.key === 'Escape') close();
    else if (ev.key === 'ArrowLeft') step(-1);
    else if (ev.key === 'ArrowRight') step(1);
  });

  document.body.append(overlay);

  overlayParts = { img, caption, counter, prev, next };
}

let overlayParts: {
  img: HTMLElement;
  caption: HTMLElement;
  counter: HTMLElement;
  prev: HTMLElement;
  next: HTMLElement;
} | null = null;

function render(): void {
  const photo = photos[index];
  if (!photo || !overlayParts) return;

  const { img, caption, counter, prev, next } = overlayParts;
  img.setAttribute('src', photo.url);
  // The caption doubles as alt text when there is one; otherwise say who posted
  // it, so a screen reader gets something better than an empty image.
  img.setAttribute('alt', photo.caption || `Photo by ${photo.uploaderName}`);
  caption.textContent = photo.caption || '';
  counter.textContent = photos.length > 1 ? `${index + 1} of ${photos.length}` : '';

  const solo = photos.length < 2;
  prev.hidden = solo;
  next.hidden = solo;
}
